import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import axios from 'axios';
import { BASE_URL } from '../../constants/config';
import { pushDataLayerEvent } from '../../utils/dataLayer';
import {
  Root,
  HeaderSection,
  IconCircle,
  LockIcon,
  Title,
  Subtitle,
  DividerRow,
  DividerLine,
  LotusIcon,
  BodyBlock,
  SuccessBox,
  SuccessIcon,
  SuccessTitle,
  ErrorAlert,
  Form,
  CardContainer,
  StripeMountPoint,
  CardLoadingOverlay,
  LoadingStateBox,
  StyledCircularProgress,
  LoadingText,
  StripeSecurityNote,
  StripeSecurityText,
  SmallLockIcon,
  ButtonGroup,
  BackButton,
  SubmitButton,
  Overlay,
  OverlayText,
  DialogPaperStyles,
} from './styles';
import {
  SUCCESS_DELAY_MS,
  CREDENTIALS_ERROR_MSG,
  NOT_INITIALIZED_MSG,
  VERIFY_ERROR_MSG,
  SUCCESS_TITLE,
  LOADING_FORM_TEXT,
  VERIFYING_TEXT,
  VERIFYING_OVERLAY_TEXT,
  NO_PAYMENT_TEXT,
  FREE_CONSULTATION_SUBTITLE,
  STRIPE_SECURITY_NOTE,
} from './constants';
import { useStripeCard } from './hooks/useStripeCard';

function CredentialsErrorView() {
  return <ErrorAlert severity="error">{CREDENTIALS_ERROR_MSG}</ErrorAlert>;
}

function SuccessView() {
  return (
    <SuccessBox>
      <SuccessIcon />
      <SuccessTitle>{SUCCESS_TITLE}</SuccessTitle>
    </SuccessBox>
  );
}

function PaymentHeader({ title }) {
  return (
    <HeaderSection>
      <IconCircle>
        <LockIcon />
      </IconCircle>
      <Title>{title}</Title>
      <Subtitle>{FREE_CONSULTATION_SUBTITLE}</Subtitle>
      <DividerRow>
        <DividerLine />
        <LotusIcon />
        <DividerLine />
      </DividerRow>
    </HeaderSection>
  );
}

function PaymentFormView({
  containerId,
  stripeReady,
  loadError,
  submitError,
  loading,
  embedded,
  onBack,
  submitButtonText,
  onSubmit,
}) {
  return (
    <>
      <BodyBlock component="div">{NO_PAYMENT_TEXT}</BodyBlock>

      {(loadError || submitError) && (
        <ErrorAlert severity="error">{loadError || submitError}</ErrorAlert>
      )}

      <Form component="form" onSubmit={onSubmit}>
        <CardContainer>
          <StripeMountPoint id={containerId} />
          {!stripeReady && (
            <CardLoadingOverlay>
              <StyledCircularProgress />
              <LoadingText>{LOADING_FORM_TEXT}</LoadingText>
            </CardLoadingOverlay>
          )}
        </CardContainer>

        <StripeSecurityNote>
          <SmallLockIcon />
          <StripeSecurityText>{STRIPE_SECURITY_NOTE}</StripeSecurityText>
        </StripeSecurityNote>

        {stripeReady && (
          <ButtonGroup $embedded={embedded}>
            {embedded && onBack && (
              <BackButton variant="outlined" onClick={onBack} disabled={loading}>
                Back
              </BackButton>
            )}
            <SubmitButton
              type="submit"
              variant="contained"
              disabled={loading}
              $embedded={embedded}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null
              }
            >
              {loading ? VERIFYING_TEXT : submitButtonText}
            </SubmitButton>
          </ButtonGroup>
        )}

        {loading && (
          <Overlay>
            <LoadingStateBox>
              <StyledCircularProgress />
              <OverlayText>{VERIFYING_OVERLAY_TEXT}</OverlayText>
            </LoadingStateBox>
          </Overlay>
        )}
      </Form>
    </>
  );
}

const StripePaymentVerification = ({
  open,
  onClose,
  onVerificationSuccess,
  patientEmail,
  patientData,
  embedded = false,
  onBack,
  submitButtonText = 'Verify Payment Method',
  title = 'Verify Payment Method',
  isModal = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const enabled = embedded || open;

  const { stripeRef, cardElementRef, containerId, stripeReady, loadError } = useStripeCard({
    enabled,
  });

  // Fire dataLayer event when the payment form becomes active (user reaches
  // the credit-card page). Embedded mode mounts on the modal Payment step;
  // dialog mode opens via `open` prop.
  useEffect(() => {
    if (enabled) {
      pushDataLayerEvent('free_consult_payment_start', { embedded });
    }
    // Intentionally only depend on `enabled` so the event fires on each
    // transition into the payment screen, not on prop churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handleSubmit = useCallback(
    async (event) => {
      if (event) event.preventDefault();

      if (!stripeRef.current || !cardElementRef.current) {
        setSubmitError(NOT_INITIALIZED_MSG);
        return;
      }

      setLoading(true);
      setSubmitError(null);

      try {
        // 1. Create a PaymentMethod from the card element
        const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: cardElementRef.current,
          billing_details: {
            name: `${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim(),
            email: patientEmail || patientData?.email || '',
            phone: patientData?.phone || '',
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        // 2. Send paymentMethodId to backend to attach to customer
        const response = await axios.post(`${BASE_URL}/payments/verify-card`, {
          paymentMethodId: paymentMethod.id,
          email: patientEmail || patientData?.email,
          firstName: patientData?.firstName || '',
          lastName: patientData?.lastName || '',
          phone: patientData?.phone || '',
          practitionerName: patientData?.practitionerName || '',
          practitionerId: patientData?.practitionerId || '',
          practitionerEmail: patientData?.practitionerEmail || '',
          practitionerUserId: patientData?.practitionerUserId || '',
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Verification failed');
        }

        setSuccess(true);
        pushDataLayerEvent('free_consult_submit');
        onVerificationSuccess(response.data.data);

        if (!embedded && onClose) {
          setTimeout(() => {
            setSuccess(false);
            setLoading(false);
            onClose();
          }, SUCCESS_DELAY_MS);
        }
      } catch (err) {
        setSubmitError(err.response?.data?.message || err.message || VERIFY_ERROR_MSG);
        setLoading(false);
      }
    },
    [patientEmail, patientData, onVerificationSuccess, embedded, onClose, stripeRef, cardElementRef]
  );

  const renderContent = () => {
    if (!publishableKey) {
      return <CredentialsErrorView />;
    }
    if (success) {
      return <SuccessView />;
    }
    return (
      <PaymentFormView
        containerId={containerId}
        stripeReady={stripeReady}
        loadError={loadError}
        submitError={submitError}
        loading={loading}
        embedded={embedded}
        onBack={onBack}
        submitButtonText={submitButtonText}
        onSubmit={handleSubmit}
      />
    );
  };

  if (embedded) {
    return (
      <Root $isModal={isModal}>
        <PaymentHeader title={title} />
        {renderContent()}
      </Root>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: DialogPaperStyles }}
    >
      <DialogContent>
        <PaymentHeader title={title} />
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentVerification;
